// External
import { useCallback, useRef } from 'react';

// Internal
import {
  addressAreEqualV2,
  buildAmountBreakdownV2,
  normalizeAddressForMerchantV2,
} from '@/lib/utils/shipping';
import type { CartAmounts, CartShippingMethod } from '@/generated/graphql';
import type {
  Breakdown,
  EmptyObject,
  MappedAddressValues,
  OnShippingAddressChangeActions,
  OnShippingAddressChangeData,
} from '@/lib/utils/shipping/types';
import { logger } from '@/lib/logger';

type onShippingAddressChangeCallback =
  | ((
      payload: OnShippingAddressChangeData,
      actions: OnShippingAddressChangeActions,
    ) => void)
  | undefined;
type executeFunction = (
  shippingAddress: MappedAddressValues | EmptyObject,
  toggleLoadingSpinner: (spinner: string | null) => void,
) => Promise<boolean>;

const PATCH_OP = {
  add: 'add',
  replace: 'replace',
};

// TODO: Cleanup once shared shipping cb hooks are used
export const useShippingAddressChangeCallback = (
  cartId: string,
  shippingMethods: CartShippingMethod[],
  amounts: CartAmounts,
): executeFunction | null => {
  const onShippingAddressChangeCb = window?.xprops
    ?.onShippingAddressChange as onShippingAddressChangeCallback;
  const merchantShippingAddressRef = useRef({});

  const execute = useCallback<executeFunction>(
    async (shippingAddress, toggleLoadingSpinner) => {
      const shippingAddressForMerchant =
        normalizeAddressForMerchantV2(shippingAddress);

      const hasAddressChanged = !addressAreEqualV2(
        shippingAddressForMerchant,
        merchantShippingAddressRef.current,
      );

      if (!hasAddressChanged) {
        logger.info('cssc_v2_address_callback_skipped_unchanged', {
          cartId,
        });
        return false;
      }

      const { total } = amounts;

      const onShippingAddressChangeParams = {
        amount: {
          breakdown: buildAmountBreakdownV2(amounts as Breakdown),
          currencyCode: total?.currencyCode,
          value: total?.currencyValue,
        },
        event:
          shippingMethods && shippingMethods.length > 0
            ? PATCH_OP.replace
            : PATCH_OP.add,
        orderID: cartId,
        shippingAddress: shippingAddressForMerchant,
      };

      // Update our current ref for the
      //shipping address we notified the merchant with
      merchantShippingAddressRef.current = shippingAddressForMerchant;

      const actions = {
        reject: (errorMessage: string) => {
          /**
           * Validating error messages are happening on the sdk so
           * we just need to pass these values.
           *
           *  {
           *    "Your order can't be shipped to this address.": 'ADDRESS_ERROR',
           *    "Your order can't be shipped to this country.": 'COUNTRY_ERROR',
           *    "Your order can't be shipped to this state.": 'STATE_ERROR',
           *    "Your order can't be shipped to this zip.": 'ZIP_ERROR',
           *  };
           *
           */
          return Promise.reject(errorMessage);
        },
      };

      try {
        toggleLoadingSpinner('spinner.fetchingShipping');
        await onShippingAddressChangeCb?.(
          onShippingAddressChangeParams as unknown as OnShippingAddressChangeData,
          actions as unknown as OnShippingAddressChangeActions,
        );

        logger.info('cssc_v2_address_callback_success', {
          cartId,
        });
        return true;
      } catch (error) {
        toggleLoadingSpinner(null);
        return Promise.reject(error);
      }
    },
    [cartId, amounts, merchantShippingAddressRef.current],
  );

  const hasCallback = typeof onShippingAddressChangeCb === 'function';

  if (!hasCallback || !cartId) {
    logger.info('cssc_v2_address_callback_not_available', {
      hasCallback,
      hasCartId: !!cartId,
    });
    return null;
  }

  return execute;
};
